<template>
  <div class="login-form">
    <v-snackbar
      v-model="show"
      top
      right
      :color="notification.color"
    >
      {{ notification.message }}

      <template #action="{ attrs }">
        <v-btn
          color="white"
          icon
          v-bind="attrs"
          @click="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <v-form
      ref="form"
      @submit.prevent="submit"
    >
      <v-card
        class="rounded-0"
        style="box-shadow: 2px 2px 4px 0px #2a3244 !important;"
      >
        <v-col class="col-12 text-center text-uppercase font-weight-bold btn-bg-gunmetal py-5">
          {{ productName }}
        </v-col>
      </v-card>
      <v-card
        class="mx-auto pa-5"
        outlined
        tile
        elevation="6"
      >
        <v-card-title class="my-1 py-7">
          <v-img
            contain
            height="38px"
            position="center"
            :src="logoUrl"
          />
        </v-card-title>

        <v-card-text
          v-if="!emailSendSuccessful"
          class="mt-6 pb-0"
        >
          <p>
            Enter the email address associated with your
            <span class="brand-text">{{ brandName }}</span> account.
          </p>
          <v-alert
            v-if="formError"
            color="red"
            type="error"
          >
            {{ formError }}
          </v-alert>
          <v-text-field
            v-model="email"
            prepend-inner-icon="mdi-email"
            label="Email"
            variant="outlined"
            color="primary"
            required
            @input="v$.email.$touch()"
            @blur="v$.email.$touch()"
          />
          <!-- ./username -->
        </v-card-text>
        <v-card-text v-else>
          <p>
            Check you email to process your new password.
          </p>
          <p>
            Thank you.
          </p>
        </v-card-text>

        <v-card-actions class="mb-3 px-4">
          <v-btn
            v-if="!emailSendSuccessful"
            type="submit"
            color="primary"
            size="large"
            block
            class="login-btn"
            :loading="formProcessing"
          >
            <template #prepend>
              <v-icon>mdi-email-fast-outline</v-icon>
            </template>
            Send Recovery Password
          </v-btn>
          <v-btn
            v-else
            color="primary"
            size="large"
            block
            class="login-btn"
            to="{ name: 'login'}"
          >
            Ok
          </v-btn>
        </v-card-actions>
        <div class="text-center">
          <span class="text-caption font-weight-thin text-center">{{ version }}</span>
        </div>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import logoUrl from '@/assets/hyphen/logo.png';

export default {
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      logoUrl,
      email: '',
      emailSendSuccessful: false,
      formError: '',
      formProcessing: false,
      show: false,
    };
  },
  computed: {
    ...mapState('notification', ['notification']),
    ...mapGetters('app', ['assetPath', 'brandName', 'name', 'version']),
    ...mapGetters('settings', ['productName']),
  },
  watch: {
    notification(value) {
      this.show = value;
    },
  },
  validations: {
    email: { required, email },
  },

  methods: {
    ...mapActions('currentUser', ['logUserIn']),
    checkFormErrors() {
      let error;
      if (!this.v$.email.$dirty) {
        error = false;
      }
      if (!this.v$.email.email) {
        this.formError = 'Must be valid e-mail';
        error = true;
      }
      if (!this.v$.email.email) {
        this.formError = 'E-mail is required';
        error = true;
      }
      return error;
    },
    setFormState(processing = true, message = '') {
      this.formProcessing = processing;
      this.formError = message;
    },
    async submit() {
      if (!this.checkFormErrors()) {
        this.setFormState();
        try {
          await this.$axios.get('/sanctum/csrf-cookie');
          await this.$axios.post('/api/forgot-password', { email: this.email });

          // this.$router.push({ name: 'dashboard-home' });
          this.setFormState(true);
          this.emailSendSuccessful = true;
        } catch (error) {
          if (error.response.data.errors) {
            const errors = Object.keys(error.response.data.errors)
              .map((e) => error.response.data.errors[e]);
            this.setFormState(
              false,
              String(errors.shift()),
            );
          } else {
            this.setFormState(
              false,
              error.response.data.message,
            );
          }
        }
      }
    },
    resetForm() {
      this.email = '';
    },
  },
};
</script>

<style>
.login-form {
  width: 480px;
}
.login-form .v-card {
  box-shadow: 2px 2px 4px 0px grey !important;
}
.login-form .login-btn {
  background-color: #205e86 !important;
  color: white !important;
  height: 3.5rem !important;
  font-size: 1.1rem !important;
  font-weight: 800 !important;
}
.login-form .login-btn .v-icon {
  color: white !important;
  font-size: 2.2rem !important;
  font-weight: 400 !important;
}
.login-form .v-field__prepend-inner {
  padding-top: 0 !important;
}
.login-form .v-field__prepend-inner .v-icon {
  color: #205e86 !important;
  font-size: 2rem !important;
  opacity: 1 !important;
}
.login-form .v-field .v-label {
  padding: 0.5rem 1rem;
  height: auto;
}
.login-form .v-field--focused .v-label {
  color: #205e86 !important;
}
.login-form .v-field__input {
  padding: 0rem 1rem !important;
  min-height: 50px !important;
}
.login-form .v-field {
  background-color: #e9eaec !important;
  border-radius: 4px;
  min-height: 60px;
}
.login-form .v-field--variant-outlined .v-field__outline {
  --v-field-border-opacity: 1;
}
.login-form .v-field--focused .v-field__outline {
  color: #205e86 !important;
  --v-field-border-width: 2px;
}
.brand-text {
  text-transform: capitalize;
}
</style>
